// Category keys constant - use these as values in forms/database
// Import from constants/categories.js for the authoritative list
import { CATEGORIES as IMPORTED_CATEGORIES } from '../constants/categories';
import { formatPrice, getCurrencyName } from './currency';

// Re-export for backward compatibility
export const CATEGORY_KEYS = IMPORTED_CATEGORIES;

// Re-export formatPrice for convenience
export { formatPrice, getCurrencyName };

// ========================================
// 🌍 Arabic Digit & Locale Formatters
// ========================================

/**
 * Check if language is Arabic
 */
export const isArabic = (lang) => (lang || "").toLowerCase().startsWith("ar");

/**
 * Convert Western digits to Arabic/Eastern Arabic numerals
 */
const toArabicDigits = (input) => {
  const s = String(input ?? "");
  const map = {
    "0": "٠", "1": "١", "2": "٢", "3": "٣", "4": "٤",
    "5": "٥", "6": "٦", "7": "٧", "8": "٨", "9": "٩",
    ",": "،"
  };
  return s.replace(/[0-9,]/g, (c) => map[c] ?? c);
};

/**
 * Format money with proper locale and currency label
 * Arabic: "١٧٬٠٠٠ جنيه سوداني"
 * English: "SDG 17,000"
 * 
 * Supports multiple call signatures for backward compatibility:
 * - formatMoney({ amount, currency, lang }) - New preferred signature
 * - formatMoney(amount, { locale, currency }) - Legacy signature
 * - formatMoney(amount, currency, lang) - Legacy signature
 * 
 * Returns null if amount is null/undefined (caller should handle display)
 */
export function formatMoney(amountOrOptions, optionsOrCurrency, langArg) {
  let amount, currency, lang;
  
  // Detect call signature
  if (amountOrOptions && typeof amountOrOptions === 'object' && 'amount' in amountOrOptions) {
    // New signature: formatMoney({ amount, currency, lang })
    amount = amountOrOptions.amount;
    currency = amountOrOptions.currency;
    lang = amountOrOptions.lang;
  } else if (typeof optionsOrCurrency === 'object' && optionsOrCurrency !== null) {
    // Legacy signature: formatMoney(amount, { locale, currency })
    amount = amountOrOptions;
    currency = optionsOrCurrency.currency;
    const locale = optionsOrCurrency.locale || 'en';
    lang = locale.startsWith('ar') ? 'ar' : 'en';
  } else {
    // Legacy signature: formatMoney(amount, currency, lang)
    amount = amountOrOptions;
    currency = optionsOrCurrency;
    lang = langArg || 'en';
  }
  
  // Return null for missing prices - don't default to 0
  if (amount === null || amount === undefined || amount === '') {
    return null;
  }
  
  const a = Number(amount);
  if (isNaN(a)) return null;
  
  const lAr = isArabic(lang);
  const code = String(currency || "SDG").toUpperCase();

  const formatted = new Intl.NumberFormat(lAr ? "ar" : "en", {
    maximumFractionDigits: 0,
  }).format(a);

  const curLabel = lAr ? getCurrencyName(code, "ar") : code;
  const finalNumber = lAr ? toArabicDigits(formatted) : formatted;

  // Arabic: number first then currency label
  return lAr ? `${finalNumber} ${curLabel}` : `${code} ${finalNumber}`;
}

/**
 * Format number with locale (views, counts, etc.)
 */
export function formatNumber({ value, lang }) {
  const lAr = isArabic(lang);
  const formatted = new Intl.NumberFormat(lAr ? "ar" : "en", {
    maximumFractionDigits: 0
  }).format(Number(value ?? 0));
  return lAr ? toArabicDigits(formatted) : formatted;
}

/**
 * Format phone number with Arabic digits if needed
 */
export function formatPhone({ phone, lang }) {
  if (!phone) return "";
  return isArabic(lang) ? toArabicDigits(phone) : String(phone);
}

/**
 * Format date with proper locale
 */
export function formatDate({ date, lang }) {
  if (!date) return "";
  const d = new Date(date);
  const lAr = isArabic(lang);
  const formatted = new Intl.DateTimeFormat(lAr ? "ar" : "en", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(d);
  return lAr ? toArabicDigits(formatted) : formatted;
}

// Get translated category name with fallback
export function getCategoryLabel(categoryKey, t) {
  if (!categoryKey || !t) return categoryKey || '';
  
  // Always try the cat.{key} format first
  const translated = t(`cat.${categoryKey}`, { defaultValue: "" });
  if (translated && translated !== `cat.${categoryKey}`) {
    return translated;
  }
  
  // If translation not found, format the key nicely (snake_case to Title Case)
  console.warn(`Missing translation for category: ${categoryKey}`);
  return categoryKey
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
